import React, { useState, useEffect } from 'react';
import { X, DownloadSimple, FilePdf, FileXls, ListChecks } from '@phosphor-icons/react';

const PREFS_KEY = 'mealExportPrefs.v1';

const loadPrefs = () => {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    // Only restore known fields so a corrupted blob can't break the modal.
    return {
      format: p.format === 'excel' ? 'excel' : 'pdf',
      style: p.style === 'detailed' ? 'detailed' : 'compact',
      includeNutrition: p.includeNutrition !== false,
      includeShopping: p.includeShopping !== false,
      showEaterNames: p.showEaterNames !== false,
      lang: p.lang === 'hi' ? 'hi' : (p.lang === 'en' ? 'en' : null),
    };
  } catch (_) { return null; }
};

export default function ExportMenuModal({ api, prefs, onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const defaultTo = new Date(Date.now() + 6 * 86400000).toISOString().slice(0, 10);

  const stored = loadPrefs();

  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(defaultTo);
  const [format, setFormat] = useState(stored?.format || 'pdf');
  const [style, setStyle] = useState(stored?.style || 'compact');
  const [includeNutrition, setIncludeNutrition] = useState(stored?.includeNutrition !== false);
  const [includeShopping, setIncludeShopping] = useState(stored?.includeShopping !== false);
  const [showEaterNames, setShowEaterNames] = useState(stored?.showEaterNames !== false);
  const [lang, setLang] = useState(stored?.lang || ((prefs?.kitchen_language === 'hi') ? 'hi' : 'en'));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // Persist toggles on every change. Date range is intentionally NOT stored —
  // every print should start from "today" rather than a stale historic range.
  useEffect(() => {
    try {
      localStorage.setItem(PREFS_KEY, JSON.stringify({
        format, style, includeNutrition, includeShopping, showEaterNames, lang,
      }));
    } catch (_) { /* localStorage disabled / full — silently ignore */ }
  }, [format, style, includeNutrition, includeShopping, showEaterNames, lang]);

  const setPreset = (days) => {
    const base = new Date();
    if (days === 'today') { setDateFrom(today); setDateTo(today); }
    else if (days === 'tomorrow') { setDateFrom(tomorrow); setDateTo(tomorrow); }
    else if (days === 'thisWeek') {
      const d = new Date();
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1);
      const mon = new Date(d.setDate(diff));
      const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
      setDateFrom(mon.toISOString().slice(0, 10));
      setDateTo(sun.toISOString().slice(0, 10));
    }
    else if (days === 'nextWeek') {
      const d = new Date();
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1) + 7;
      const mon = new Date(d.setDate(diff));
      const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
      setDateFrom(mon.toISOString().slice(0, 10));
      setDateTo(sun.toISOString().slice(0, 10));
    }
    else if (typeof days === 'number') {
      const end = new Date(base);
      end.setDate(end.getDate() + days - 1);
      setDateFrom(base.toISOString().slice(0, 10));
      setDateTo(end.toISOString().slice(0, 10));
    }
  };

  const dayCount = (() => {
    try {
      const a = new Date(dateFrom);
      const b = new Date(dateTo);
      return Math.round((b - a) / 86400000) + 1;
    } catch (_) { return 0; }
  })();

  const handleDownload = async () => {
    setErr('');
    if (!dateFrom || !dateTo) { setErr('Pick a date range.'); return; }
    if (new Date(dateTo) < new Date(dateFrom)) { setErr('End date must be on/after start date.'); return; }
    if (dayCount > 31) { setErr('Range cannot exceed 31 days.'); return; }
    setBusy(true);
    try {
      const res = await api.post('/meals/export-range', {
        date_from: dateFrom,
        date_to: dateTo,
        format,
        style,
        lang,
        include_nutrition: includeNutrition,
        include_shopping_list: includeShopping,
        show_eater_names: showEaterNames,
      }, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const langSuffix = lang === 'hi' ? '_hi' : '';
      a.download = `menu_${dateFrom}_${dateTo}${langSuffix}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      onClose();
    } catch (e) {
      let msg = e?.response?.data?.detail || e?.message || 'Export failed.';
      if (e?.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          const parsed = JSON.parse(txt);
          msg = parsed.detail || msg;
        } catch (_) { /* ignore */ }
      }
      setErr(typeof msg === 'string' ? msg : 'Export failed.');
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="export-menu-modal"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        className="w-full max-w-lg rounded-2xl overflow-hidden"
        style={{ background: '#1A1612', border: '1px solid rgba(255,107,53,0.25)' }}>
        <div className="px-5 py-4 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, rgba(255,107,53,0.15), rgba(255,107,53,0.05))', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-2">
            <DownloadSimple size={18} weight="fill" className="text-[#FF6B35]" />
            <h3 className="text-base font-bold text-white">Export Menu</h3>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-white" data-testid="export-close-btn">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5 max-h-[75vh] overflow-y-auto">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-2 block">Quick pick</label>
            <div className="flex flex-wrap gap-1.5">
              {[
                { k: 'today', label: 'Today' },
                { k: 'tomorrow', label: 'Tomorrow' },
                { k: 'thisWeek', label: 'This week' },
                { k: 'nextWeek', label: 'Next week' },
                { k: 3, label: '3 days' },
                { k: 7, label: '7 days' },
                { k: 14, label: '14 days' },
              ].map(p => (
                <button key={p.label} onClick={() => setPreset(p.k)}
                  data-testid={`export-preset-${typeof p.k === 'number' ? p.k + 'd' : p.k}`}
                  className="px-2.5 py-1 rounded-full text-[10px] font-bold border bg-white/5 text-neutral-300 border-white/10 hover:bg-[#FF6B35]/15 hover:text-[#FF6B35] hover:border-[#FF6B35]/40 transition-colors">
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1 block">From</label>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                data-testid="export-date-from"
                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:border-[#FF6B35] outline-none" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1 block">To</label>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                data-testid="export-date-to"
                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:border-[#FF6B35] outline-none" />
            </div>
          </div>
          <p className="text-[11px] text-neutral-500 -mt-3">
            <span className={`font-semibold ${dayCount > 31 || dayCount < 1 ? 'text-amber-400' : 'text-[#FF6B35]'}`}>{dayCount}</span> day(s) selected
            {dayCount > 31 && <span className="text-amber-400 ml-2">· Max 31 days</span>}
          </p>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-2 block">Format</label>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setFormat('pdf')} data-testid="export-format-pdf"
                className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold border transition-colors ${format === 'pdf' ? 'bg-[#FF6B35]/15 text-[#FF6B35] border-[#FF6B35]/40' : 'bg-white/5 text-neutral-300 border-white/10 hover:bg-white/10'}`}>
                <FilePdf size={16} weight={format === 'pdf' ? 'fill' : 'regular'} /> PDF
              </button>
              <button onClick={() => setFormat('excel')} data-testid="export-format-excel"
                className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold border transition-colors ${format === 'excel' ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' : 'bg-white/5 text-neutral-300 border-white/10 hover:bg-white/10'}`}>
                <FileXls size={16} weight={format === 'excel' ? 'fill' : 'regular'} /> Excel
              </button>
            </div>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-2 block">Design</label>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setStyle('compact')} data-testid="export-style-compact"
                className={`text-left p-3 rounded-xl border transition-colors ${style === 'compact' ? 'bg-[#FF6B35]/10 border-[#FF6B35]/40' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}>
                <p className="text-sm font-bold text-white">Compact</p>
                <p className="text-[10px] text-neutral-500 mt-0.5">One-page grid · fridge-ready</p>
              </button>
              <button onClick={() => setStyle('detailed')} data-testid="export-style-detailed"
                className={`text-left p-3 rounded-xl border transition-colors ${style === 'detailed' ? 'bg-purple-500/10 border-purple-500/40' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}>
                <p className="text-sm font-bold text-white">Detailed</p>
                <p className="text-[10px] text-neutral-500 mt-0.5">Day-per-page · with images</p>
              </button>
            </div>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-2 block">Add-ons (AI · uses your Universal key)</label>
            <div className="space-y-2">
              <label className="flex items-start gap-2 cursor-pointer p-2.5 rounded-lg hover:bg-white/5">
                <input type="checkbox" checked={includeNutrition} onChange={e => setIncludeNutrition(e.target.checked)}
                  data-testid="export-nutrition-toggle" className="mt-0.5 accent-[#FF6B35]" />
                <div className="flex-1">
                  <p className="text-sm text-white font-semibold">Nutrition / calorie estimate</p>
                  <p className="text-[10px] text-neutral-500">Per-dish kcal · P · C · F (cached after first generation)</p>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer p-2.5 rounded-lg hover:bg-white/5">
                <input type="checkbox" checked={includeShopping} onChange={e => setIncludeShopping(e.target.checked)}
                  data-testid="export-shopping-toggle" className="mt-0.5 accent-[#FF6B35]" />
                <div className="flex-1 flex items-start gap-2">
                  <ListChecks size={16} className="text-emerald-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-white font-semibold">Auto shopping list</p>
                    <p className="text-[10px] text-neutral-500">AI aggregates ingredients across the range, grouped by category</p>
                  </div>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer p-2.5 rounded-lg hover:bg-white/5">
                <input type="checkbox" checked={showEaterNames} onChange={e => setShowEaterNames(e.target.checked)}
                  data-testid="export-eater-names-toggle" className="mt-0.5 accent-[#FF6B35]" />
                <div className="flex-1">
                  <p className="text-sm text-white font-semibold">Show eater names on multi-eater days</p>
                  <p className="text-[10px] text-neutral-500">Prints &quot;Dira: Paneer Butter Masala (mild)&quot; etc. so the cook knows who eats what. Off = clean profile-less menu.</p>
                </div>
              </label>
            </div>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-2 block">Print in (for the cook)</label>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setLang('en')} data-testid="export-lang-en"
                className={`py-2 rounded-xl text-sm font-bold border transition-colors ${lang === 'en' ? 'bg-[#FF6B35]/15 text-[#FF6B35] border-[#FF6B35]/40' : 'bg-white/5 text-neutral-300 border-white/10 hover:bg-white/10'}`}>
                English
              </button>
              <button onClick={() => setLang('hi')} data-testid="export-lang-hi"
                title="Translates via AI on first download; per-dish translations cached forever"
                className={`py-2 rounded-xl text-sm font-bold border transition-colors ${lang === 'hi' ? 'bg-[#FF6B35]/15 text-[#FF6B35] border-[#FF6B35]/40' : 'bg-white/5 text-neutral-300 border-white/10 hover:bg-white/10'}`}>
                हिन्दी
              </button>
            </div>
          </div>

          {err && (
            <div className="rounded-lg p-2.5 text-[11px] text-red-300" style={{ background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.30)' }}>
              {err}
            </div>
          )}
        </div>

        <div className="p-4 flex gap-2" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <button onClick={onClose} disabled={busy}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold bg-white/5 text-neutral-300 border border-white/10 hover:bg-white/10 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={handleDownload} disabled={busy || dayCount < 1 || dayCount > 31} data-testid="export-download-btn"
            className="flex-[2] py-2.5 rounded-xl text-sm font-bold bg-[#FF6B35] text-white hover:bg-[#E85D2A] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
            {busy ? (
              <>
                <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                {lang === 'hi' ? 'Translating + generating…' : 'Generating…'}
              </>
            ) : (
              <>
                <DownloadSimple size={16} weight="bold" />
                Download {format === 'excel' ? 'Excel' : 'PDF'} {lang === 'hi' ? '(हिन्दी)' : ''} ({dayCount}d)
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
